import stringify from 'json-stringify-safe';
import merge from 'lodash/merge';

import APICall from '$ReduxLegacy/Actions/APICall';
import Unireducer from '$ReduxLegacy/Reducers/Factories/Unireducer';
import {
    HVSentry,
    checkIfFormErrorAndThrow,
    flexibleDateString,
    throwError
} from '$Utils';

// We may want to move this duck to a shared place since I suspect we'll be using it in a lot of places.

// Consider moving to utils??
export const _isFailure = data => data.type === 'API_FAILURE';

// if we pass this, we'll get the generic error
// so we can pass metadata through
const genericError = {
    _error: 'Sorry, an error occurred. Please try again later.'
};

//Cycling through the other actions
export const getNextFormAndSetActive =
    ({ review, loggedIn = false }) =>
    async (dispatch, getState) => {
        const currentState =
            getState() && (getState().activeForm || getState());

        const formsData = await _getForms(review, dispatch, getState);

        if (_isFailure(formsData)) return formsData;

        const {
            response: { results: formList }
        } = formsData;

        // If no forms are returned, the user is done!
        // Mark as complete and bail early
        if (formList.length === 0) {
            dispatch({
                type: '@@DYNAMIC_FORMS/CHANGE',
                payload: 'complete'
            });
            return;
        }

        const mainForm = formList[0];
        const activeForm = await _getEntireForm(
            mainForm,
            { loggedIn },
            dispatch
        );
        if (!activeForm) return;
        if (
            currentState &&
            currentState.id &&
            currentState.id !== activeForm.id
        ) {
            //make sure previous form exists: check for id
            activeForm.previousForm = currentState;
            currentState.completedUpToIndex = currentState.lastSection;
        }
        return dispatch({
            type: '@@DYNAMIC_FORMS/CHANGE',
            payload: activeForm
        });
    };

export const getSpecificFormAndSetActive =
    ({ definitionUUID }, options = {}) =>
    async dispatch => {
        const definitionData = await _getDefinition(definitionUUID, dispatch);
        const mainForm = definitionData.response;
        const activeForm = await _getEntireForm(mainForm, options, dispatch);
        if (!activeForm) {
            //full-screen error, asking patient to try again?
            return null;
        }
        return dispatch({
            type: '@@DYNAMIC_FORMS/CHANGE',
            payload: activeForm
        });
    };

export const getSpecificFormWithOrgAndSetActive =
    ({ definitionUUID, orgID, formEntryUUID }) =>
    async dispatch => {
        const definitionData = await _getDefinitionWithOrg(
            definitionUUID,
            orgID,
            dispatch
        );
        if (!definitionData) return null;
        const mainForm = definitionData.response;
        const activeForm = await _getEntireForm(
            mainForm,
            { loggedIn: true, orgID: orgID, formEntryUUID: formEntryUUID },
            dispatch
        );
        if (!activeForm) {
            //full-screen error, asking patient to try again?
            return null;
        }
        return dispatch({
            type: '@@DYNAMIC_FORMS/CHANGE',
            payload: activeForm
        });
    };

export const submitPartial =
    (values, options = {}) =>
    (dispatch, getState) => {
        const currentState =
            (getState() && getState().activeForm) || getState();
        const { definitionID, fobiFormID, activeSectionIndex, fields } =
            currentState;

        const { loggedIn = false, orgID = null } = options;

        const prepareError = info => {
            return {
                error: genericError,
                metadata: { info: info, state: stringify(getState()) }
            };
        };

        //Redux Form's asyncValidate requires a plain object throw (NOT an error),
        //  so we need to use throwError(null) instead of checkIfFormErrorAndThrow

        // Logged In users are used in Patient Portal version
        if (loggedIn && !orgID) {
            console.error('No OrgID found.');
            return throwError(null)(prepareError('No OrgID found.'));
        }

        if (!definitionID) {
            console.error('No definition ID found.');
            return throwError(null)(prepareError('No definition ID found.'));
        }
        if (!fobiFormID) {
            console.error('No fobiFormID found.');
            return throwError(null)(prepareError('No fobiFormID found.'));
        }
        if (activeSectionIndex === undefined) {
            console.error('No active section index found.');
            return throwError(null)(
                prepareError('No active section index found.')
            );
        }
        if (!fields) {
            console.error('No fields found in state.');
            return throwError(null)(prepareError('No fields found in state.'));
        }

        //If field is in current section and has a value, set result[name] = value
        const currentSectionValues = fields
            .filter(
                ({ section, name }) =>
                    section === activeSectionIndex && values[name]
            )
            .reduce((result, { name }) => {
                result[name] = values[name];
                return result;
            }, {});

        return dispatch(
            APICall({
                type: 'PATCH',
                url: loggedIn
                    ? `/api/v2/forms/form-li/${orgID}/`
                    : '/api/v2/forms/form/',
                data: {
                    form_definition: definitionID,
                    fobi_form: fobiFormID,
                    data: JSON.stringify(currentSectionValues)
                }
            })
        ).then(data => {
            if (_isFailure(data)) return throwError(null)(data);
            return incrementActiveSection()(dispatch, getState);
        });
    };

export const submitForm =
    (values, options = {}) =>
    (dispatch, getState) => {
        // `orgID` is related to whether or not the user is loggedIn.
        // `formEntryUUID` is related to whether or not this is an Invited Form, which
        // is a subset of logged in forms.
        const {
            review = false,
            loggedIn = false,
            orgID = null,
            formEntryUUID = null
        } = options;
        const currentState = getState().activeForm || getState();

        const prepareError = info => {
            return {
                error: genericError,
                metadata: { info: info, state: stringify(getState()) }
            };
        };

        const { definitionID, fobiFormID } = currentState;
        if (!definitionID || !fobiFormID) {
            console.error('No definition ID or fobiFormID found.');
            return throwError(null)(
                prepareError('No definition ID or fobiFormID found.')
            );
        }
        if (loggedIn && !orgID) {
            console.error('No org ID found for logged in User.');
            return throwError(null)(
                prepareError('No org ID found for logged in User.')
            );
        }

        // The standard submit. Used in Dynamic Registration.
        let url = '/api/v2/forms/form/';

        if (loggedIn) {
            // if `formEntryUUID` is present, this is an invited form and should be
            // PUT to that specific URL. Otherwise, POST to the generic logged in URL.
            url = formEntryUUID
                ? `/api/v2/forms/form-li/invited/${orgID}/${formEntryUUID}/`
                : `/api/v2/forms/form-li/${orgID}/`;
        }

        return dispatch(
            APICall({
                type: formEntryUUID ? 'PUT' : 'POST',
                url,
                data: {
                    form_definition: definitionID,
                    fobi_form: fobiFormID,
                    data: JSON.stringify(values)
                }
            })
        ).then(data => {
            //Will catch and throw error if there's a problem, or just return out.
            if (_isFailure(data)) {
                return checkIfFormErrorAndThrow(data);
            }
            //Patient Portal
            if (loggedIn) return { type: 'API_SUCCESS' };
            //dReg
            return getNextFormAndSetActive({ review })(dispatch, getState);
        });
    };

export const clearForms = () => dispatch => {
    return dispatch({
        type: '@@DYNAMIC_FORMS/CLEAR'
    });
};

export const decrementActiveSection = () => (dispatch, getState) => {
    const currentState = getState().activeForm || getState();
    const { activeSectionIndex, previousForm } = currentState;
    const newIndex = activeSectionIndex - 1;
    //If we're going back from the first section, go to the previous form
    if (newIndex < 0) {
        if (!previousForm) {
            return console.error('There is no previous form to go back to');
        }
        return dispatch({
            type: '@@DYNAMIC_FORMS/CHANGE',
            payload: previousForm
        });
    }
    //return to the previous section
    return setActiveSection(newIndex)(dispatch, getState);
};

export const incrementActiveSection = () => (dispatch, getState) => {
    const currentState = getState().activeForm || getState();
    const { activeSectionIndex, lastSection } = currentState;
    const newIndex = activeSectionIndex + 1;
    //If we're all out of forms, mark complete
    if (newIndex > lastSection) {
        return dispatch({
            type: '@@DYNAMIC_FORMS/CHANGE',
            payload: 'complete'
        });
    }
    //advance to the next section
    return setActiveSection(newIndex)(dispatch, getState);
};

export const setActiveSection = newIndex => (dispatch, getState) => {
    // anything with the getState() and the keyname (e.g. 'activeForm') is kinda brittle
    // if this was truly done in the reducer, we could scope the state.
    // Hmmmm :thinking:
    if (!Number.isInteger(newIndex)) {
        console.error('Passed index is not an integer.');
        return getState();
    }
    const currentState = getState().activeForm || getState();

    // If we're moving further than before (active index higher than completed
    //  index), update completed index

    if (currentState.activeSectionIndex > currentState.completedUpToIndex) {
        currentState.completedUpToIndex = currentState.activeSectionIndex;
    }
    const updatedSectionForm = merge({}, currentState, {
        activeSectionIndex: newIndex
    });

    return dispatch({
        type: '@@DYNAMIC_FORMS/CHANGE',
        payload: updatedSectionForm
    });
};

// For internal use-- exported *only* for TESTING!
// If you try to use these directly as actions, they will *fail*

export const _getForms = (review, dispatch) => {
    return dispatch(
        APICall({
            type: 'GET',
            url: '/api/v2/forms/',
            data: {
                review: !!review
            }
        })
    );
};

export const _getEntireForm = async (mainForm, options = {}, dispatch) => {
    if (!mainForm || !mainForm.fobi_form) return null;
    const {
        fobi_form: { slug }
    } = mainForm;

    const definitionID = mainForm.id;
    const fobiFormID = mainForm.fobi_form.id;

    // Fetch detail for form:
    const detailData = await _getDetail(slug, dispatch);
    if (_isFailure(detailData)) return null;
    const detail = detailData.response;

    // Partials and previous entries of the form are retrieved:
    const partial = await _getPartial(
        definitionID,
        fobiFormID,
        options,
        dispatch
    );
    if (partial == null) return null; // partial load failed (e.g. 500)
    return _prepareForm(detail, mainForm, partial, false);
};

export const _getDefinition = (uuid, dispatch) => {
    const truncatedUUID = uuid.substring(0, 8);
    return dispatch(
        APICall({
            type: 'GET',
            url: `/api/v2/forms/registration/form/${truncatedUUID}/`
        })
    );
};

// Patient Portal Util call
export const _getDefinitionWithOrg = (uuid, orgID, dispatch) => {
    const truncatedUUID = uuid.substring(0, 8);
    return dispatch(
        APICall({
            type: 'GET',
            url: `/api/v2/forms/form/${orgID}/${truncatedUUID}/`
        })
    );
};

export const _getDetail = (slug, dispatch) => {
    return dispatch(
        APICall({
            type: 'OPTIONS',
            url: `/api/v2/fobi/fobi-form-entry/${slug}/`
        })
    );
};

export const _getPartial = async (
    definitionID,
    fobiFormID,
    options = {},
    dispatch
) => {
    const { loggedIn, orgID, formEntryUUID } = options;
    // return {} if 404, null if failed.
    const partialData = loggedIn
        ? await _getLoggedInEntry(
              definitionID,
              fobiFormID,
              orgID,
              formEntryUUID,
              dispatch
          )
        : await _getEntry(definitionID, fobiFormID, dispatch);

    // The entry endpoint will return a 404 if there are no
    // previous entries or partials.
    // This is expected behavior and we must handle it.

    if (_isFailure(partialData)) {
        if (partialData.error.status !== 404) {
            //Non-blocking error in UI saying partial (possibly) failed.
            //Put error in GA
            return null;
        }
        if (!!formEntryUUID) {
            // form entry uuids indicates invited forms.
            // We want to return null for 404s in this case.
            return null;
        }
        return {}; //404, expected response when no partial exists
    }
    return partialData.response.data;
};

export const _getEntry = (definitionID, fobiFormID, dispatch) => {
    return dispatch(
        APICall({
            type: 'GET',
            url: `/api/v2/forms/form/${definitionID}/${fobiFormID}`
        })
    );
};

export const _getLoggedInEntry = (
    definitionID,
    fobiFormID,
    orgID,
    formEntryUUID,
    dispatch
) => {
    if (!!formEntryUUID) {
        // If we already have a pre-existing HVFormEntry uuid, pass it explicitly.
        // Both endpoints hit LoggedInPatientHVFormEntryCreateView.
        return dispatch(
            APICall({
                type: 'GET',
                url: `/api/v2/forms/form-li/invited/${orgID}/${formEntryUUID}/`
            })
        );
    } else {
        return dispatch(
            APICall({
                type: 'GET',
                url: `/api/v2/forms/form-li/${orgID}/${definitionID}/${fobiFormID}/`
            })
        );
    }
};

export const _prepareForm = (detail, mainForm, partial, isReviewPage) => {
    let activeForm = mainForm.fobi_form;
    activeForm.uuid = mainForm.form_definition_uuid;
    activeForm.definitionID = mainForm.id;
    activeForm.fobiFormID = activeForm.id;
    activeForm.previousForm = null;
    activeForm.completedUpToIndex = -1;
    activeForm.kind = mainForm.kind;
    const { ordered_form_fields } = mainForm;
    let values = partial || {};
    let initialValues = isReviewPage ? {} : values;
    let sectionCounter = 0;
    activeForm.fields = ordered_form_fields.map(name => {
        const field = detail.actions.PUT[name];

        if (!field) {
            console.error(
                `Field order/object mismatch; Field ${name} not found in detail API`
            );
            return null;
        }
        // Have to parse field extras here to determine initialValues.
        let fieldExtras = {};
        try {
            if (field.field_extras) {
                const isString =
                    typeof field.field_extras === 'string' ||
                    field.field_extras instanceof String;

                fieldExtras = isString
                    ? JSON.parse(field.field_extras)
                    : field.field_extras;
            }
        } catch (e) {
            // Send to JS for now; will monitor
            HVSentry.sendExceptionToReactSentry(e);
            console.error(e);
        }
        // If this is a review page, we should prep the values for display
        let value = null;
        let choices;
        if (isReviewPage) {
            value = values[name];
        }
        // Define the choices correctly

        if (field.choices) {
            choices = _prepareChoices(field.choices);
        }

        // Is it a section break?
        let isSectionBreak = false;
        // capture section count first
        field.section = sectionCounter;
        if (field.type === 'content' && field.content === '<p>page_break</p>') {
            isSectionBreak = true;
            sectionCounter++;
        }
        // If there is no value already
        // and there is an autodate field extra,
        // Pre-populate the form with today's date
        // in ISO format.
        // Has to be done at the duck level
        // so initialValues will be calculated right.
        if (
            fieldExtras.autodate &&
            field.type === 'date' &&
            !initialValues[name]
        ) {
            initialValues[name] = flexibleDateString(new Date(), null, 'ISO');
        }

        let finalField = Object.assign({}, field, {
            name,
            choices,
            isSectionBreak,
            value
        });

        // Add fieldExtras to the definition, even if empty
        finalField.fieldExtras = fieldExtras;

        return finalField;
    });

    if (!isReviewPage) {
        activeForm.initialValues = initialValues;
        activeForm.lastSection = sectionCounter;
        activeForm.activeSectionIndex = 0;
    } else {
        //Remove page breaks
        activeForm.fields = activeForm.fields.filter(
            field => field.value != null
        );
    }
    return activeForm;
};

export const _prepareChoices = choices => {
    if (!choices) return undefined;
    return choices.map(choice => {
        return [choice.value, choice.display_name];
    });
};

export default Unireducer('@@DYNAMIC_FORMS', null);
